import api from '../api/api';

export interface SubscriptionPlan {
  id: number;
  name: string;
  description: string;
  price_per_term: number;
  price_per_year: number;
  max_students: number;
  max_cameras: number;
  features: Record<string, boolean>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ActivationToken {
  id: number;
  token: string;
  plan_id: number;
  subscription_type: 'term' | 'year';
  is_used: boolean;
  used_by_school_id?: number;
  used_at?: string;
  expires_at: string;
  created_by: number;
  created_at: string;
  plan?: SubscriptionPlan;
  used_by_school?: any;
  created_by_user?: any;
}

export interface SchoolSubscription {
  id: number;
  school_id: number;
  plan_id: number;
  subscription_type: 'term' | 'year';
  start_date: string;
  end_date: string;
  status: 'active' | 'expired' | 'cancelled';
  payment_status: 'pending' | 'paid' | 'failed';
  payment_method: string;
  activation_token?: string;
  activated_at?: string;
  activated_by?: number;
  created_at: string;
  updated_at: string;
  school?: any;
  plan?: SubscriptionPlan;
  activated_by_user?: any;
}

export interface SchoolSubscriptionStatus {
  school_id: number;
  school_name: string;
  subscription_status: string;
  active_subscription?: SchoolSubscription;
  can_use_students: boolean;
  can_use_cameras: boolean;
  current_student_count: number;
  current_camera_count: number;
  max_students?: number;
  max_cameras?: number;
}

export interface AcademicTerm {
  id: number;
  name: string;
  year: number;
  term_number: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TokenActivationRequest {
  token: string;
  school_id: number;
}

export interface TokenActivationResponse {
  success: boolean;
  message: string;
  subscription?: SchoolSubscription;
}

export interface SubscriptionStats {
  total_schools: number;
  active_subscriptions: number;
  expired_subscriptions: number;
  no_subscription: number;
  total_revenue_kes: number;
  subscription_rate: number;
}

// Subscription Plans
export const getSubscriptionPlans = async (activeOnly: boolean = true) => {
  const response = await api.get(`/subscriptions/plans?active_only=${activeOnly}`);
  return response.data;
};

export const createSubscriptionPlan = async (plan: Omit<SubscriptionPlan, 'id' | 'created_at' | 'updated_at'>) => {
  const response = await api.post('/subscriptions/plans', plan);
  return response.data;
};

export const updateSubscriptionPlan = async (planId: number, updates: Partial<SubscriptionPlan>) => {
  const response = await api.put(`/subscriptions/plans/${planId}`, updates);
  return response.data;
};

export const deleteSubscriptionPlan = async (planId: number) => {
  const response = await api.delete(`/subscriptions/plans/${planId}`);
  return response.data;
};

// Activation Tokens
export const getActivationTokens = async (params?: {
  created_by?: number;
  is_used?: boolean;
  skip?: number;
  limit?: number;
}): Promise<ActivationToken[]> => {
  const response = await api.get('/subscriptions/tokens', { params });
  return response.data as ActivationToken[];
};

export const getActivationToken = async (tokenId: number): Promise<ActivationToken> => {
  const response = await api.get(`/subscriptions/tokens/${tokenId}`);
  return response.data as ActivationToken;
};

export const createActivationToken = async (tokenData: {
  plan_id: number;
  subscription_type: 'term' | 'year';
  expires_at?: string;
}): Promise<ActivationToken> => {
  const response = await api.post('/subscriptions/tokens', tokenData);
  return response.data as ActivationToken;
};

export const updateActivationToken = async (
  tokenId: number,
  tokenData: {
    plan_id?: number;
    subscription_type?: 'term' | 'year';
    expires_at?: string;
    is_used?: boolean;
  }
): Promise<ActivationToken> => {
  const response = await api.put(`/subscriptions/tokens/${tokenId}`, tokenData);
  return response.data as ActivationToken;
};

export const deleteActivationToken = async (tokenId: number): Promise<void> => {
  await api.delete(`/subscriptions/tokens/${tokenId}`);
};

export const activateToken = async (request: TokenActivationRequest) => {
  const response = await api.post('/subscriptions/tokens/activate', request);
  return response.data;
};

// School Subscriptions
export const getSchoolSubscriptionStatus = async (schoolId: number) => {
  const response = await api.get(`/subscriptions/schools/${schoolId}/subscription`);
  return response.data;
};

export const getSchoolSubscriptions = async (schoolId: number, params?: {
  skip?: number;
  limit?: number;
}) => {
  const queryParams = new URLSearchParams();
  if (params?.skip) queryParams.append('skip', params.skip.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  
  const response = await api.get(`/subscriptions/schools/${schoolId}/subscriptions?${queryParams.toString()}`);
  return response.data;
};

// Academic Terms
export const getAcademicTerms = async (params?: {
  year?: number;
  is_active?: boolean;
  skip?: number;
  limit?: number;
}) => {
  const queryParams = new URLSearchParams();
  if (params?.year) queryParams.append('year', params.year.toString());
  if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
  if (params?.skip) queryParams.append('skip', params.skip.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  
  const response = await api.get(`/subscriptions/terms?${queryParams.toString()}`);
  return response.data;
};

export const getCurrentAcademicTerm = async () => {
  const response = await api.get('/subscriptions/terms/current');
  return response.data;
};

export const createAcademicTerm = async (term: Omit<AcademicTerm, 'id' | 'created_at' | 'updated_at'>) => {
  const response = await api.post('/subscriptions/terms', term);
  return response.data;
};

export const updateAcademicTerm = async (termId: number, updates: Partial<AcademicTerm>) => {
  const response = await api.put(`/subscriptions/terms/${termId}`, updates);
  return response.data;
};

export const deleteAcademicTerm = async (termId: number) => {
  const response = await api.delete(`/subscriptions/terms/${termId}`);
  return response.data;
};

// Statistics
export const getSubscriptionStats = async () => {
  const response = await api.get('/subscriptions/stats');
  return response.data;
}; 